import { useEffect, useState } from 'react';
import {
  BLOOD_GROUPS, RH_FACTORS,
  type PublicDonor, type BloodGroup, type RhFactor, type DonorSex,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePagedList } from '../hooks/usePagedList';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';

interface DeferralStatus {
  deferred: boolean; type: 'temporary' | 'permanent' | null;
  until: string | null; reason: string | null;
}

const NOT_DEFERRED: DeferralStatus = { deferred: false, type: null, until: null, reason: null };

const EMPTY = {
  nik: '', name: '', dateOfBirth: '', sex: 'male' as DonorSex,
  abo: '' as '' | BloodGroup, rh: '' as '' | RhFactor,
  phone: '', city: '',
};

export function Donors() {
  const { can } = useAuth();
  const list = usePagedList<PublicDonor>('/donors');

  const [deferrals, setDeferrals] = useState<Record<string, DeferralStatus>>({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** A NIK mismatch message awaiting staff acknowledgement. */
  const [mismatch, setMismatch] = useState<string | null>(null);

  /**
   * Deferral status for the rows on this page only — ten lookups instead of one per donor
   * in the database. "Not eligible" is a normal answer rather than an error, so a failure
   * here degrades one cell instead of failing the table.
   */
  useEffect(() => {
    if (list.items.length === 0) {
      setDeferrals({});
      return;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(list.items.map(async (x) => {
        const s = await api.get<DeferralStatus>(`/donors/${x.id}/deferral`).catch(() => NOT_DEFERRED);
        return [x.id, s] as const;
      }));
      if (!cancelled) setDeferrals(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [list.items]);

  function openForm() {
    setForm(EMPTY);
    setFormError(null);
    setMismatch(null);
    setOpen(true);
  }

  async function submit(e: React.FormEvent, acknowledge: boolean) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    if (!acknowledge) setMismatch(null);
    try {
      await api.post('/donors', {
        nik: form.nik, name: form.name, dateOfBirth: form.dateOfBirth, sex: form.sex,
        abo: form.abo || null, rh: form.rh || null,
        phone: form.phone || null, city: form.city || null,
        acknowledgeNikMismatch: acknowledge,
      });
      setOpen(false);
      setMismatch(null);
      setNotice('Pendonor terdaftar. NIK disimpan sebagai hash, bukan plaintext.');
      list.refresh();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422
          && (err.details as { requiresAcknowledgement?: boolean } | undefined)?.requiresAcknowledgement) {
        // Not an outright rejection — civil registry data genuinely can be out of sync.
        setMismatch(err.message);
        return;
      }
      setFormError(err instanceof ApiError ? err.message : 'Gagal mendaftarkan pendonor.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Pendonor</h2>
        <p>
          NIK tidak pernah disimpan utuh. Yang tersimpan hanya hash-nya dan empat digit
          terakhir untuk verifikasi di depan pendonor — jadi pencarian NIK hanya cocok
          pada empat digit itu.
        </p>
      </div>

      {notice && <div className="msg ok">{notice}</div>}

      <DataTable
        list={list}
        searchPlaceholder="Cari nama, 4 digit NIK, HP, atau kota…"
        action={can('donor:write') ? { label: 'Daftarkan pendonor', onClick: openForm } : undefined}
        empty="Belum ada pendonor terdaftar."
        emptyFiltered="Tidak ada pendonor yang cocok dengan pencarian."
        columns={['Nama', 'NIK', 'Golongan', 'Kota', 'Kelayakan']}
      >
        {list.items.map((d) => {
          const s = deferrals[d.id];
          return (
            <tr key={d.id}>
              <td>{d.name}</td>
              <td className="mono muted">…{d.nikLast4}</td>
              <td className="mono">
                {d.abo ? `${d.abo}${d.rh === 'positive' ? '+' : '−'}` : '—'}
              </td>
              <td className="muted">{d.city ?? '—'}</td>
              <td>
                {s?.deferred ? (
                  <span className="pill accent" title={s.reason ?? undefined}>
                    {s.type === 'permanent' ? 'ditangguhkan permanen' : `sampai ${s.until?.slice(0, 10)}`}
                  </span>
                ) : (
                  <span className="pill">layak</span>
                )}
              </td>
            </tr>
          );
        })}
      </DataTable>

      <Modal
        open={open}
        title="Daftarkan pendonor"
        description="NIK dicocokkan dengan tanggal lahir dan jenis kelamin sebelum disimpan."
        onClose={() => setOpen(false)}
        onSubmit={(e) => void submit(e, false)}
        footer={mismatch ? (
          <>
            <button type="button" onClick={() => setMismatch(null)} disabled={saving}>
              Perbaiki dulu
            </button>
            {/* Deliberately not a submit button: pressing Enter must never be what
                acknowledges a mismatch. */}
            <button type="button" className="primary" disabled={saving}
              onClick={(e) => void submit(e, true)}>
              {saving ? 'Menyimpan…' : 'Data sudah benar, lanjutkan'}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setOpen(false)} disabled={saving}>Batal</button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Menyimpan…' : 'Daftarkan'}
            </button>
          </>
        )}
      >
        <div className="stack" style={{ gap: '0.9rem' }}>
          {formError && <div className="msg error">{formError}</div>}

          {mismatch && (
            <div className="msg error">
              <strong>NIK tidak cocok dengan data yang diisi.</strong>
              <p style={{ margin: '0.35rem 0 0' }}>{mismatch}</p>
              <p style={{ margin: '0.35rem 0 0', fontSize: 13 }}>
                Periksa ulang ketikannya. Kalau data kependudukan memang berbeda dari NIK,
                lanjutkan — ketidakcocokan ini tercatat di jejak audit.
              </p>
            </div>
          )}

          <div className="grid-2">
            <div className="field">
              <label className="label" htmlFor="p-nik">NIK (16 digit)</label>
              <input id="p-nik" value={form.nik} required inputMode="numeric" maxLength={16}
                onChange={(e) => setForm({ ...form, nik: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="p-name">Nama sesuai KTP</label>
              <input id="p-name" value={form.name} required
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="p-dob">Tanggal lahir</label>
              <input id="p-dob" type="date" value={form.dateOfBirth} required
                onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="p-sex">Jenis kelamin</label>
              <select id="p-sex" value={form.sex}
                onChange={(e) => setForm({ ...form, sex: e.target.value as DonorSex })}>
                <option value="male">Laki-laki</option>
                <option value="female">Perempuan</option>
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="p-abo">Golongan darah</label>
              <select id="p-abo" value={form.abo}
                onChange={(e) => setForm({ ...form, abo: e.target.value as '' | BloodGroup })}>
                <option value="">Belum diperiksa</option>
                {BLOOD_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="p-rh">Rhesus</label>
              <select id="p-rh" value={form.rh} disabled={!form.abo}
                onChange={(e) => setForm({ ...form, rh: e.target.value as '' | RhFactor })}>
                <option value="">—</option>
                {RH_FACTORS.map((f) => (
                  <option key={f} value={f}>{f === 'positive' ? 'Positif' : 'Negatif'}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="p-phone">Nomor HP</label>
              <input id="p-phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="p-city">Kota</label>
              <input id="p-city" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
